import React, { useEffect, useState } from 'react'
import useCompanyItemStore from '@store/useCompanyItemStore'
import companyItemService from '@services/companyItemService'
import {
  Download,
  RefreshCw,
  FileText,
  List,
  BookOpen,
  Folder,
  Filter,
  AlertCircle,
  Search,
  Archive,
  LayoutGrid,
  LayoutList,
} from 'lucide-react'
import { SearchBar, Select, Button, PageHeader } from '@components/common'
import ResourceView from './ResourceView'
import './Resources.css'

/**
 * Company Resources Page
 *
 * Displays resources (policies, procedures, templates, etc.) assigned
 * to the company by Super Admin.
 */
const Resources = () => {
  const {
    items,
    loading,
    filters,
    pagination,
    stats,
    fetchItems,
    fetchStats,
    selectItem,
    downloadItem,
    updateToLatestVersion,
    setSearch,
    setTypeFilter,
    nextPage,
    prevPage,
  } = useCompanyItemStore()

  const [showViewModal, setShowViewModal] = useState(false)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchItems()
    fetchStats()
  }, [filters, pagination.page])

  const handleView = (item) => {
    selectItem(item)
    setShowViewModal(true)
  }

  const handleDownload = (item) => {
    downloadItem(item.item_id)
  }

  const handleUpdateVersion = async (item) => {
    await updateToLatestVersion(item.item_id)
  }

  const getTypeIcon = (type) => {
    const icons = {
      policy: <FileText size={20} />,
      procedure: <List size={20} />,
      template: <FileText size={20} />,
      guideline: <BookOpen size={20} />,
      resource: <Folder size={20} />,
    }
    return icons[type] || <FileText size={20} />
  }

  const getTypeColor = (type) => {
    const colors = {
      policy: '#3b82f6',
      procedure: '#10b981',
      template: '#8b5cf6',
      guideline: '#f59e0b',
      resource: '#6366f1',
    }
    return colors[type] || '#6b7280'
  }

  const pluralizeType = (type) => {
    const plurals = {
      policy: 'Policies',
      procedure: 'Procedures',
      template: 'Templates',
      guideline: 'Guidelines',
      resource: 'Resources',
    }
    return plurals[type] || `${type.charAt(0).toUpperCase() + type.slice(1)}s`
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...companyItemService.getItemTypes().map((t) => ({ value: t.value, label: t.label })),
  ]

  // Check if there are any resources by type
  const hasTypeDistribution = stats.byType && Object.values(stats.byType).some((count) => count > 0)

  return (
    <div className="resources-page">
      <PageHeader
        title="Resources"
        description="Access policies, procedures, templates, and guidelines assigned to your company"
        breadcrumbs={[{ label: 'Resources' }]}
      />

      {/* Statistics Cards */}
      <div className="resources-stats-grid">
        <div className="resources-stat-card">
          <div className="resources-stat-card__icon resources-stat-card__icon--primary">
            <Archive size={24} />
          </div>
          <div className="resources-stat-card__content">
            <div className="resources-stat-card__label">Total Resources</div>
            <div className="resources-stat-card__value">{stats.total || 0}</div>
          </div>
        </div>
        {stats.with_updates > 0 && (
          <div className="resources-stat-card">
            <div className="resources-stat-card__icon resources-stat-card__icon--warning">
              <AlertCircle size={24} />
            </div>
            <div className="resources-stat-card__content">
              <div className="resources-stat-card__label">Updates Available</div>
              <div className="resources-stat-card__value">{stats.with_updates}</div>
            </div>
          </div>
        )}
      </div>

      {/* Type Distribution */}
      {hasTypeDistribution && (
        <div className="resources-type-distribution">
          {Object.entries(stats.byType).map(([type, count]) => (
            <div
              key={type}
              className="resources-type-item"
              style={{ borderColor: getTypeColor(type) }}
            >
              <div
                className="resources-type-item__icon"
                style={{ backgroundColor: `${getTypeColor(type)}20`, color: getTypeColor(type) }}
              >
                {getTypeIcon(type)}
              </div>
              <div className="resources-type-item__content">
                <div className="resources-type-item__label">{pluralizeType(type)}</div>
                <div className="resources-type-item__value">{count}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="resources-filters">
        <SearchBar
          value={filters.search}
          onChange={setSearch}
          placeholder="Search resources..."
          loading={loading}
        />

        <div className="resources-filters__divider">
          <Filter size={18} />
        </div>

        <Select
          value={filters.type}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={typeOptions}
        />

        <div className="resources-filters__view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'grid' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <LayoutGrid size={16} strokeWidth={2} />
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <LayoutList size={16} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="resources-loading">Loading resources...</div>
      ) : items.length === 0 ? (
        <div className="resources-empty">
          <div className="resources-empty__icon">
            <Search size={48} />
          </div>
          <h3>No Resources Found</h3>
          <p>No resources have been assigned to your company yet.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="resources-grid">
          {items.map((item) => (
            <div key={item.id} className="resource-card" onClick={() => handleView(item)}>
              {item.has_update && (
                <div className="resource-card__update-badge">
                  <AlertCircle size={12} />
                  Update available
                </div>
              )}

              <div className="resource-card__header">
                <div
                  className="resource-card__icon"
                  style={{
                    backgroundColor: `${getTypeColor(item.item_type)}20`,
                    color: getTypeColor(item.item_type),
                  }}
                >
                  {getTypeIcon(item.item_type)}
                </div>
                <div className="resource-card__title-section">
                  <h3 className="resource-card__title">{item.title}</h3>
                  <span
                    className="resource-card__type"
                    style={{
                      backgroundColor: `${getTypeColor(item.item_type)}20`,
                      color: getTypeColor(item.item_type),
                    }}
                  >
                    {item.item_type.charAt(0).toUpperCase() + item.item_type.slice(1)}
                  </span>
                </div>
              </div>

              {item.description && (
                <p className="resource-card__description">{item.description}</p>
              )}

              <div className="resource-card__meta">
                <span className="resource-card__version">v{item.item_version}</span>
                <span className="resource-card__date">{formatDate(item.item_updated_at)}</span>
              </div>

              <div className="resource-card__actions">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownload(item)
                  }}
                >
                  <Download size={14} />
                  Download
                </Button>
                {item.has_update && (
                  <Button
                    size="small"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUpdateVersion(item)
                    }}
                  >
                    <RefreshCw size={14} />
                    Update
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="resources-list">
          {items.map((item) => (
            <div key={item.id} className="resource-list-item" onClick={() => handleView(item)}>
              <div
                className="resource-list-item__icon"
                style={{
                  backgroundColor: `${getTypeColor(item.item_type)}20`,
                  color: getTypeColor(item.item_type),
                }}
              >
                {getTypeIcon(item.item_type)}
              </div>

              <div className="resource-list-item__content">
                <h3 className="resource-list-item__title">{item.title}</h3>
                <div className="resource-list-item__meta">
                  <span
                    className="resource-list-item__type"
                    style={{
                      backgroundColor: `${getTypeColor(item.item_type)}20`,
                      color: getTypeColor(item.item_type),
                    }}
                  >
                    {item.item_type.charAt(0).toUpperCase() + item.item_type.slice(1)}
                  </span>
                  <span className="resource-list-item__version">v{item.item_version}</span>
                  <span className="resource-list-item__size">
                    {companyItemService.formatFileSize(item.file_size)}
                  </span>
                  <span className="resource-list-item__date">{formatDate(item.item_updated_at)}</span>
                </div>
              </div>

              {item.has_update && (
                <div className="resource-list-item__update-badge">
                  <AlertCircle size={12} />
                  Update
                </div>
              )}

              <div className="resource-list-item__actions">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownload(item)
                  }}
                >
                  <Download size={14} />
                  Download
                </Button>
                {item.has_update && (
                  <Button
                    size="small"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUpdateVersion(item)
                    }}
                  >
                    <RefreshCw size={14} />
                    Update
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="resources-pagination">
          <div className="resources-pagination__info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </div>
          <div className="resources-pagination__controls">
            <Button
              variant="secondary"
              onClick={prevPage}
              disabled={pagination.page === 1 || loading}
              size="small"
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={nextPage}
              disabled={pagination.page >= pagination.totalPages || loading}
              size="small"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <ResourceView
          onClose={() => setShowViewModal(false)}
        />
      )}
    </div>
  )
}

export default Resources
